import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core";
import Popover from "@material-ui/core/Popover";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import { LineHeightIcon } from "components/icons/components/LineHeightIcon";
import { LetterSpaceIcon } from "components/icons/components/LetterSpaceIcon";
import { FontCDNData } from "pages/flyer/builder/services/s3CustomFontService";
import { SaveBrandKitFontPresetInput, UpdateBrandKitFontPresetInput } from "graphQL/mutations/brandKit";

// ─── Types ───────────────────────────────────────────────────────────────────

interface FontFamily {
  baseName: string;
  variants: FontCDNData[];
}

interface BrandKitFontPresetData {
  id: string;
  label: string;
  fontFamily: string;
  fontVariant: string;
  fontSize: number;
  lineHeight: number;
  letterSpacing: number;
}

interface AddBrandFontModalProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  initialFamily?: FontFamily | null;
  editPreset?: BrandKitFontPresetData | null;
  fontFamilies: FontFamily[];
  onSave: (input: SaveBrandKitFontPresetInput) => void;
  onUpdate?: (input: UpdateBrandKitFontPresetInput) => void;
  // When true, the Font Family dropdown is readonly — the user can't switch
  // to another font. Used after uploading a font to set that specific file
  // as a brand font preset.
  lockFamily?: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const FONT_SIZE_OPTIONS = [
  "8",
  "9",
  "10",
  "11",
  "12",
  "14",
  "16",
  "18",
  "20",
  "22",
  "24",
  "26",
  "28",
  "36",
  "48",
  "72",
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getVariantLabel(variantName: string, baseName: string): string {
  const suffix = variantName.slice(baseName.length).replace(/^[-_]/, "");
  if (!suffix) return "Regular";
  // Capitalize first letter
  return suffix.charAt(0).toUpperCase() + suffix.slice(1);
}

// ─── Component ───────────────────────────────────────────────────────────────

const AddBrandFontModal: React.FC<AddBrandFontModalProps> = ({
  open,
  anchorEl,
  onClose,
  initialFamily,
  editPreset,
  fontFamilies,
  onSave,
  onUpdate,
  lockFamily = false,
}) => {
  const classes = useStyles();
  const isEditMode = Boolean(editPreset);

  const [selectedFamily, setSelectedFamily] = useState<FontFamily | null>(null);
  const [selectedVariant, setSelectedVariant] = useState("");
  const [fontSize, setFontSize] = useState("16");
  const [lineHeight, setLineHeight] = useState("1");
  const [letterSpacing, setLetterSpacing] = useState("1");
  const [fontName, setFontName] = useState("");

  const [familyDropdownOpen, setFamilyDropdownOpen] = useState(false);
  const [variantDropdownOpen, setVariantDropdownOpen] = useState(false);
  const [sizeDropdownOpen, setSizeDropdownOpen] = useState(false);
  const [familySearchTerm, setFamilySearchTerm] = useState("");
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const familyDropdownRef = useRef<HTMLDivElement>(null);
  const variantDropdownRef = useRef<HTMLDivElement>(null);
  const sizeDropdownRef = useRef<HTMLDivElement>(null);
  const familySearchInputRef = useRef<HTMLInputElement>(null);

  // Initialize state when modal opens
  useEffect(() => {
    if (!open) return;

    if (editPreset) {
      // Edit mode: populate from existing preset
      const family = fontFamilies.find((f) => f.baseName === editPreset.fontFamily) || null;
      setSelectedFamily(family);
      // Parse italic/underline flags from fontVariant (e.g. "Roboto-Bold:i:u")
      const parts = editPreset.fontVariant.split(":");
      setSelectedVariant(parts[0]);
      setIsItalic(parts.includes("i"));
      setIsUnderline(parts.includes("u"));
      setFontSize(String(editPreset.fontSize));
      setLineHeight(String(editPreset.lineHeight));
      setLetterSpacing(String(editPreset.letterSpacing));
      setFontName(editPreset.label || editPreset.fontFamily);
    } else if (initialFamily) {
      // Add mode: pre-select the initial family
      setSelectedFamily(initialFamily);
      // Default to Regular variant
      const regular = initialFamily.variants.find((v) => {
        const suffix = v.name.slice(initialFamily.baseName.length).replace(/^[-_]/, "").toLowerCase();
        return !suffix || suffix === "regular" || suffix === "normal" || suffix === "reg";
      });
      setSelectedVariant((regular || initialFamily.variants[0])?.name || "");
      setIsItalic(false);
      setIsUnderline(false);
      setFontSize("16");
      setLineHeight("1");
      setLetterSpacing("1");
      setFontName(initialFamily.baseName);
    } else {
      setSelectedFamily(null);
      setSelectedVariant("");
      setIsItalic(false);
      setIsUnderline(false);
      setFontSize("16");
      setLineHeight("1");
      setLetterSpacing("1");
      setFontName("");
    }
  }, [open, editPreset, initialFamily, fontFamilies]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (familyDropdownRef.current && !familyDropdownRef.current.contains(e.target as Node)) {
        setFamilyDropdownOpen(false);
      }
      if (variantDropdownRef.current && !variantDropdownRef.current.contains(e.target as Node)) {
        setVariantDropdownOpen(false);
      }
      if (sizeDropdownRef.current && !sizeDropdownRef.current.contains(e.target as Node)) {
        setSizeDropdownOpen(false);
      }
    };
    if (familyDropdownOpen || variantDropdownOpen || sizeDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [familyDropdownOpen, variantDropdownOpen, sizeDropdownOpen]);

  const handleFamilySelect = useCallback((family: FontFamily) => {
    setSelectedFamily(family);
    setFamilyDropdownOpen(false);
    // Keep the preset label in sync with the selected family. Since the manual
    // "Font name" input is disabled, the preset label is always derived from
    // the selected family's base name — so switching family here also renames
    // the card.
    setFontName(family.baseName);
    // Default to Regular variant
    const regular = family.variants.find((v) => {
      const suffix = v.name.slice(family.baseName.length).replace(/^[-_]/, "").toLowerCase();
      return !suffix || suffix === "regular" || suffix === "normal" || suffix === "reg";
    });
    setSelectedVariant((regular || family.variants[0])?.name || "");
    // Reset italic for custom fonts (italic comes from font file variants, not CSS toggle)
    const isCustom = family.variants.some((v) => v.s3Url !== "");
    if (isCustom) setIsItalic(false);
  }, []);

  const handleVariantSelect = useCallback((variantName: string) => {
    setSelectedVariant(variantName);
    setVariantDropdownOpen(false);
  }, []);

  const handleSizeSelect = useCallback((size: string) => {
    setFontSize(size);
    setSizeDropdownOpen(false);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!selectedFamily || !selectedVariant) return;
    const parsedSize = parseFloat(fontSize);
    const parsedLineHeight = parseFloat(lineHeight);
    const parsedLetterSpacing = parseFloat(letterSpacing);
    if (isNaN(parsedSize) || isNaN(parsedLineHeight) || isNaN(parsedLetterSpacing)) return;

    // Encode italic/underline flags as suffixes in fontVariant
    let encodedVariant = selectedVariant;
    if (isItalic) encodedVariant += ":i";
    if (isUnderline) encodedVariant += ":u";

    if (isEditMode && editPreset && onUpdate) {
      onUpdate({
        id: editPreset.id,
        label: fontName || selectedFamily.baseName,
        fontFamily: selectedFamily.baseName,
        fontVariant: encodedVariant,
        fontSize: parsedSize,
        lineHeight: parsedLineHeight,
        letterSpacing: parsedLetterSpacing,
      });
    } else {
      onSave({
        label: fontName || selectedFamily.baseName,
        fontFamily: selectedFamily.baseName,
        fontVariant: encodedVariant,
        fontSize: parsedSize,
        lineHeight: parsedLineHeight,
        letterSpacing: parsedLetterSpacing,
      });
    }
    onClose();
  }, [
    selectedFamily,
    selectedVariant,
    fontSize,
    lineHeight,
    letterSpacing,
    isItalic,
    isUnderline,
    isEditMode,
    editPreset,
    fontName,
    onSave,
    onUpdate,
    onClose,
  ]);

  // Determine if selected font is custom (uploaded to CDN) vs standard (Google/default)
  const isCustomFont = useMemo(() => {
    if (!selectedFamily) return false;
    return selectedFamily.variants.some((v) => v.s3Url !== "");
  }, [selectedFamily]);

  // For custom fonts, categorize variants into weights vs italic/condensed style variants
  const { weightVariants, italicVariants, condensedVariants } = useMemo(() => {
    if (!selectedFamily || !isCustomFont)
      return {
        weightVariants: [] as FontCDNData[],
        italicVariants: [] as FontCDNData[],
        condensedVariants: [] as FontCDNData[],
      };
    const weight: FontCDNData[] = [];
    const italic: FontCDNData[] = [];
    const condensed: FontCDNData[] = [];
    for (const v of selectedFamily.variants) {
      const lower = v.name.toLowerCase();
      if (lower.includes("italic") || lower.endsWith("-it") || lower.endsWith("_it")) {
        italic.push(v);
      } else if (lower.includes("condensed")) {
        condensed.push(v);
      } else {
        weight.push(v);
      }
    }
    return { weightVariants: weight, italicVariants: italic, condensedVariants: condensed };
  }, [selectedFamily, isCustomFont]);

  const variantLabel = useMemo(() => {
    if (!selectedFamily || !selectedVariant) return "Select style";
    let label = getVariantLabel(selectedVariant, selectedFamily.baseName);
    if (isItalic) label += " Italic";
    if (isUnderline) label += " Underline";
    return label;
  }, [selectedFamily, selectedVariant, isItalic, isUnderline]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{
        style: {
          width: 216,
          padding: 12,
          borderRadius: 8,
          boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
          overflow: "visible",
        },
      }}
    >
      <div className={classes.modalTitle}>{isEditMode ? "Edit Brand Font" : "Add Brand Font"}</div>

      {/* NAMING DISABLED: the manual "Font name" input is commented out —
          the preset label always defaults to the selected family's base name.
          State + default-setting logic is kept so re-enabling is a one-liner.
      <input
        type="text"
        placeholder="Font name"
        value={fontName}
        onChange={(e) => setFontName(e.target.value)}
        style={{
          width: 192,
          height: 32,
          padding: "0 8px",
          fontSize: 12,
          border: "1px solid #E2E2FC",
          borderRadius: 4,
          outline: "none",
          boxSizing: "border-box",
          fontFamily: "Inter, sans-serif",
          color: "#2E3860",
          marginBottom: 8,
        }}
      />
      */}

      {/* Font Family */}
      <div ref={familyDropdownRef} className={classes.fieldDropdownWrapper} style={{ width: 192 }}>
        <button
          className={classes.fieldDropdownBtn}
          disabled={lockFamily}
          onClick={() => {
            if (lockFamily) return;
            const opening = !familyDropdownOpen;
            setFamilyDropdownOpen(opening);
            if (opening) {
              setFamilySearchTerm("");
              setTimeout(() => familySearchInputRef.current?.focus(), 50);
            }
          }}
          style={lockFamily ? { cursor: "default", opacity: 0.8 } : undefined}
        >
          <span className={classes.fieldDropdownText}>{selectedFamily?.baseName || "Select a font"}</span>
          {!lockFamily && (
            <ChevronDownIcon
              className={classes.fieldChevron}
              style={{ transform: familyDropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }}
            />
          )}
        </button>
        {!lockFamily && familyDropdownOpen && fontFamilies.length > 0 && (
          <div className={classes.fieldDropdownList}>
            <div style={{ padding: "3px 4px", position: "sticky", top: 0, background: "white", zIndex: 1 }}>
              <input
                ref={familySearchInputRef}
                type="text"
                placeholder="Search fonts..."
                value={familySearchTerm}
                onChange={(e) => setFamilySearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  height: 32,
                  padding: "4px 8px",
                  fontSize: 12,
                  border: "1px solid #E2E2FC",
                  borderRadius: 4,
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
            {fontFamilies
              .filter((f) => f.baseName.toLowerCase().includes(familySearchTerm.toLowerCase()))
              .map((family) => (
                <button
                  key={family.baseName}
                  className={classes.fieldDropdownItem}
                  onClick={() => handleFamilySelect(family)}
                >
                  {family.baseName}
                </button>
              ))}
          </div>
        )}
      </div>

      {/* Style / Weight + Size — hidden for now */}
      <div className={classes.numericRow} style={{ display: "none" }}>
        <div className={classes.styleField}>
          <div ref={variantDropdownRef} className={classes.fieldDropdownWrapper}>
            <button
              className={classes.fieldDropdownBtn}
              onClick={() => selectedFamily && setVariantDropdownOpen(!variantDropdownOpen)}
              disabled={!selectedFamily}
            >
              <span className={classes.fieldDropdownText}>{variantLabel}</span>
              <ChevronDownIcon
                className={classes.fieldChevron}
                style={{ transform: variantDropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }}
              />
            </button>
            {variantDropdownOpen && selectedFamily && (
              <div className={classes.fieldDropdownList}>
                {isCustomFont ? (
                  <>
                    {/* Custom font: weight variants (non-italic, non-condensed) */}
                    {weightVariants.map((variant) => (
                      <button
                        key={variant.name}
                        className={classes.fieldDropdownItem}
                        onClick={() => handleVariantSelect(variant.name)}
                        style={{
                          fontWeight: selectedVariant === variant.name ? 600 : 400,
                          backgroundColor: selectedVariant === variant.name ? "#d3d3f8" : undefined,
                        }}
                      >
                        {getVariantLabel(variant.name, selectedFamily.baseName)}
                      </button>
                    ))}
                    {/* Separator if there are style variants */}
                    {(italicVariants.length > 0 || condensedVariants.length > 0) && (
                      <div style={{ height: 1, background: "#E1E6EB", margin: "4px 6px" }} />
                    )}
                    {/* Italic file variants */}
                    {italicVariants.map((variant) => (
                      <button
                        key={variant.name}
                        className={classes.fieldDropdownItem}
                        onClick={() => handleVariantSelect(variant.name)}
                        style={{
                          fontWeight: selectedVariant === variant.name ? 600 : 400,
                          backgroundColor: selectedVariant === variant.name ? "#d3d3f8" : undefined,
                        }}
                      >
                        {getVariantLabel(variant.name, selectedFamily.baseName)}
                      </button>
                    ))}
                    {/* Condensed file variants */}
                    {condensedVariants.map((variant) => (
                      <button
                        key={variant.name}
                        className={classes.fieldDropdownItem}
                        onClick={() => handleVariantSelect(variant.name)}
                        style={{
                          fontWeight: selectedVariant === variant.name ? 600 : 400,
                          backgroundColor: selectedVariant === variant.name ? "#d3d3f8" : undefined,
                        }}
                      >
                        {getVariantLabel(variant.name, selectedFamily.baseName)}
                      </button>
                    ))}
                    {/* Underline toggle (CSS property, always available) */}
                    <div style={{ height: 1, background: "#E1E6EB", margin: "4px 6px" }} />
                    <button
                      className={classes.fieldDropdownItem}
                      onClick={() => {
                        setIsUnderline((v) => !v);
                      }}
                      style={{
                        fontWeight: isUnderline ? 600 : 400,
                        backgroundColor: isUnderline ? "#d3d3f8" : undefined,
                      }}
                    >
                      Underline
                    </button>
                  </>
                ) : (
                  <>
                    {/* Standard font: CSS weight variants */}
                    {selectedFamily.variants.map((variant) => (
                      <button
                        key={variant.name}
                        className={classes.fieldDropdownItem}
                        onClick={() => handleVariantSelect(variant.name)}
                        style={{
                          fontWeight: selectedVariant === variant.name ? 600 : 400,
                          backgroundColor: selectedVariant === variant.name ? "#d3d3f8" : undefined,
                        }}
                      >
                        {getVariantLabel(variant.name, selectedFamily.baseName)}
                      </button>
                    ))}
                    {/* Separator + style toggles */}
                    <div style={{ height: 1, background: "#E1E6EB", margin: "4px 6px" }} />
                    <button
                      className={classes.fieldDropdownItem}
                      onClick={() => {
                        setIsItalic((v) => !v);
                      }}
                      style={{ fontWeight: isItalic ? 600 : 400, backgroundColor: isItalic ? "#d3d3f8" : undefined }}
                    >
                      Italic
                    </button>
                    <button
                      className={classes.fieldDropdownItem}
                      onClick={() => {
                        setIsUnderline((v) => !v);
                      }}
                      style={{
                        fontWeight: isUnderline ? 600 : 400,
                        backgroundColor: isUnderline ? "#d3d3f8" : undefined,
                      }}
                    >
                      Underline
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={classes.numericField}>
          <div ref={sizeDropdownRef} className={classes.fieldDropdownWrapper}>
            <div className={classes.sizeInputRow}>
              <input
                className={classes.sizeInput}
                type="text"
                value={fontSize}
                onChange={(e) => {
                  if (/^\d{0,4}(\.\d{0,1})?$/.test(e.target.value) || e.target.value === "") {
                    setFontSize(e.target.value);
                  }
                }}
                onFocus={() => setSizeDropdownOpen(false)}
              />
              <span className={classes.sizeUnit}>px</span>
              <ChevronDownIcon
                className={classes.sizeChevron}
                style={{ transform: sizeDropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                onClick={() => setSizeDropdownOpen(!sizeDropdownOpen)}
              />
            </div>
            {sizeDropdownOpen && (
              <div className={classes.fieldDropdownList}>
                {FONT_SIZE_OPTIONS.map((size) => (
                  <button
                    key={size}
                    className={classes.fieldDropdownItem}
                    onClick={() => handleSizeSelect(size)}
                    style={{
                      fontWeight: fontSize === size ? 600 : 400,
                      backgroundColor: fontSize === size ? "#d3d3f8" : undefined,
                    }}
                  >
                    {size}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Line Height / Letter Spacing */}
      <div className={classes.numericRow} style={{ display: "none" }}>
        <div className={classes.numericField}>
          <div className={classes.iconInputRow}>
            <div className={classes.inputIcon}>
              <LineHeightIcon style={{ width: 14, height: 14 }} />
            </div>
            {(() => {
              // Internally lineHeight is an em multiplier (what the backend expects),
              // but we render it as the px equivalent: multiplier × font size. On edit
              // we convert the typed px back to em so the save payload is unchanged.
              const fontSizePx = parseFloat(fontSize);
              const lineHeightEm = parseFloat(lineHeight);
              const lineHeightPx =
                !isNaN(lineHeightEm) && !isNaN(fontSizePx) && fontSizePx > 0
                  ? Math.round(lineHeightEm * fontSizePx * 10) / 10
                  : "";
              return (
                <input
                  className={classes.iconNumericInput}
                  type="number"
                  step="1"
                  min="0"
                  max="500"
                  value={lineHeightPx}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!(/^-?\d{0,4}(\.\d{0,1})?$/.test(value) || value === "")) return;
                    if (value === "") {
                      setLineHeight("");
                      return;
                    }
                    const parsedPx = parseFloat(value);
                    if (isNaN(parsedPx) || isNaN(fontSizePx) || fontSizePx <= 0) return;
                    const em = parsedPx / fontSizePx;
                    // Keep 4 decimals for precision without making the state string huge.
                    setLineHeight(String(Math.round(em * 10000) / 10000));
                  }}
                />
              );
            })()}
            <span className={classes.unitLabelInline}>px</span>
          </div>
        </div>
        <div className={classes.numericField}>
          <div className={classes.iconInputRow}>
            <div className={classes.inputIcon}>
              <LetterSpaceIcon style={{ width: 14, height: 14 }} />
            </div>
            <input
              className={classes.iconNumericInput}
              type="number"
              step="0.1"
              min="-7.5"
              max="50"
              value={letterSpacing}
              onChange={(e) => {
                if (/^-?\d{0,4}(\.\d{0,1})?$/.test(e.target.value) || e.target.value === "") {
                  setLetterSpacing(e.target.value);
                }
              }}
            />
            <span className={classes.unitLabelInline}>px</span>
          </div>
        </div>
      </div>

      {/* Submit button */}
      <button className={classes.submitBtn} onClick={handleSubmit} disabled={!selectedFamily || !selectedVariant}>
        {isEditMode ? "Save" : "Add Font"}
      </button>
    </Popover>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  modalTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#2E3860",
    marginBottom: 10,
  },
  fieldDropdownWrapper: {
    position: "relative" as any,
  },
  fieldDropdownBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    height: 32,
    padding: "4px 8px",
    fontSize: 12,
    color: "#2E3860",
    background: "white",
    border: "1px solid #E2E2FC",
    borderRadius: 4,
    cursor: "pointer",
    boxSizing: "border-box" as any,
    "&:hover": { borderColor: "#B0B0E8" },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  fieldDropdownText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as any,
  },
  fieldChevron: {
    width: 12,
    height: 12,
    color: "#707087",
    transition: "transform 0.15s",
    flexShrink: 0,
  },
  fieldDropdownList: {
    position: "absolute" as any,
    top: "100%",
    left: 0,
    right: 0,
    marginTop: 2,
    background: "white",
    border: "1px solid #E2E2FC",
    borderRadius: 4,
    maxHeight: 180,
    overflowY: "auto" as any,
    zIndex: 20,
    boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
    "&::-webkit-scrollbar": { width: 4 },
    "&::-webkit-scrollbar-thumb": { backgroundColor: "#C4C4D4", borderRadius: 4 },
  },
  fieldDropdownItem: {
    display: "block",
    width: "100%",
    padding: "0 8px",
    height: 24,
    lineHeight: "24px",
    fontSize: 11,
    color: "#2E3860",
    background: "transparent",
    border: "none",
    textAlign: "left" as any,
    cursor: "pointer",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as any,
    "&:hover": { background: "#E2E2FC" },
  },
  numericRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 6,
    marginTop: 6,
  },
  numericField: {
    width: 94,
    flexShrink: 0,
  },
  styleField: {
    width: 94,
    flexShrink: 0,
  },
  sizeInputRow: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: 32,
    border: "1px solid #E2E2FC",
    borderRadius: 4,
    boxSizing: "border-box" as any,
    overflow: "hidden",
    "&:focus-within": { borderColor: "#B0B0E8" },
  },
  sizeInput: {
    width: 30,
    height: "100%",
    padding: 0,
    paddingLeft: 8,
    fontSize: 12,
    color: "#2E3860",
    border: "none",
    outline: "none",
    boxSizing: "border-box" as any,
    background: "transparent",
    textAlign: "left" as any,
  },
  sizeUnit: {
    fontSize: 12,
    color: "#2E3860",
    paddingLeft: 0,
    flexShrink: 0,
  },
  sizeChevron: {
    width: 12,
    height: 12,
    color: "#707087",
    cursor: "pointer",
    transition: "transform 0.15s",
    flexShrink: 0,
    marginLeft: "auto",
    marginRight: 4,
  },
  iconInputRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 32,
    border: "1px solid #E2E2FC",
    borderRadius: 4,
    boxSizing: "border-box" as any,
    overflow: "hidden",
    "&:focus-within": { borderColor: "#B0B0E8" },
  },
  inputIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: "#2E3860",
  },
  iconNumericInput: {
    width: 30,
    height: "100%",
    padding: 0,
    fontSize: 12,
    color: "#2E3860",
    border: "none",
    outline: "none",
    boxSizing: "border-box" as any,
    textAlign: "right" as any,
    // Hide number input arrows
    "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    MozAppearance: "textfield",
  },
  unitLabelInline: {
    fontSize: 12,
    color: "#2E3860",
    flexShrink: 0,
    paddingLeft: 2,
    paddingRight: 6,
  },
  submitBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 192,
    height: 32,
    padding: 0,
    marginTop: 10,
    fontSize: 12,
    fontWeight: 600,
    color: "white",
    background: "#EE4360",
    border: "none",
    borderRadius: 12,
    cursor: "pointer",
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
});

export default AddBrandFontModal;
